<template>
    <Modal ref="modal" title="Raise Credit Note" size="is-xxxlarge" :loading="isLoading" paddingless @closed="close()">
        <div class="tw-p-3">
            <FormField label="Date" field-for="date">
                <DateInput id="date" v-model="form.date" class="input is-small" required />
            </FormField>

            <FormField label="Description" field-for="description" class="tw-mt-2">
                <Textbox v-model="form.description" name="description" maxlength="255" type="textarea" rows="2" />
            </FormField>

            <div
                v-if="form.creditable_type === 'App\\Models\\SupplierInvoice'"
                class="tw-grid md:tw-grid-cols-2 tw-gap-2 tw-mt-2"
            >
                <FormField label="Target Currency" field-for="target_currency">
                    <Select
                        id="target_currency"
                        v-model="form.target_currency"
                        disabled
                        @update:model-value="getConversionRate()"
                    >
                        <option
                            v-for="(currency, currencyCode) in store.get('available_currencies')"
                            :key="currencyCode"
                            :value="currencyCode"
                        >
                            {{ currency }}
                        </option>
                    </Select>
                </FormField>
                <FormField field-class="tw-flex tw-gap-2" label="Conversion Rate" field-for="conversion_rate">
                    <NumberInput
                        v-model="form.conversion_rate"
                        :currency="form.target_currency"
                        :disabled="form.target_currency === event?.currency_code"
                        name="conversion_rate"
                        label="Conversion Rate"
                        class="tw-flex-1"
                    />
                    <Button @click="openCurrencyConverter()">
                        <i class="mdi mdi-calculator"></i>
                    </Button>
                </FormField>
            </div>
        </div>

        <DataTable
            :data="form.credit_note_line_items"
            :columns="
                [
                    {
                        title: 'Item',
                        field: 'item',
                    },
                    {
                        title: 'Qty',
                        field: 'quantity',
                    },
                    {
                        title: 'Unit Price',
                        field: 'unit_price_target',
                    },
                    {
                        title: 'Net Total',
                        field: 'net_total',
                    },
                    {
                        title: 'Gross Total',
                        field: 'gross_total',
                    },
                    {
                        title: 'Tax',
                        field: 'tax_rate_id',
                    },
                    {
                        title: '',
                        field: 'actions',
                        class: '!tw-text-right',
                        width: 75,
                    },
                ].filter((c) => c)
            "
        >
            <template #column(item)="{ row }">
                <Select v-model="row.creditable_id" @update:model-value="(v) => onLineItemSelect(v, row)">
                    <option
                        v-for="item in availableInvoiceLineItems"
                        id="line-item"
                        :key="item.id"
                        :value="item.id"
                        :disabled="item.alreadySelected"
                    >
                        {{ item.description }}
                    </option>
                </Select>
            </template>

            <template #column(quantity)="{ row }">
                <EditableText v-model="row.quantity" type="number" :min-value="0" :decimal-places="0" />
            </template>
            <template #column(unit_price_target)="{ row }">
                <EditableText
                    v-model="row.unit_price_target"
                    type="currency"
                    :currency-symbol="formatCurrency(0, 0, form.target_currency).replace('0', '')"
                />
            </template>
            <template #column(tax_rate_id)="{ row }">
                <TaxRateSelector
                    v-model="row.tax_rate_id"
                    :type="form.creditable_type === 'App\\Models\\SupplierInvoice' ? 'expense' : 'income'"
                    nullable
                    compact
                />
            </template>
            <template #column(net_total)="{ row }">
                <span>{{ localise(getSubtotal(row), 2, true, form.target_currency) }}</span>
            </template>
            <template #column(gross_total)="{ row }">
                <span>{{ localise(getTotal(row), 2, true, form.target_currency) }}</span>
            </template>

            <template #column(actions)="{ index }">
                <Button type="is-danger" @click="form.credit_note_line_items.splice(index, 1)">
                    <i class="mdi mdi-close"></i>
                </Button>
            </template>
            <template v-if="form.credit_note_line_items.length > 1" #footer>
                <tfoot>
                    <tr>
                        <th colspan="3"></th>
                        <th>
                            {{
                                localise(
                                    form.credit_note_line_items.reduce(
                                        (a, c) => a + c.quantity * c.unit_price_target,
                                        0
                                    ),
                                    2,
                                    true,
                                    form.target_currency
                                )
                            }}
                        </th>
                        <th>
                            {{
                                localise(
                                    form.credit_note_line_items.reduce(
                                        (a, c) =>
                                            a +
                                            (c.quantity * c.unit_price_target +
                                                ((c.quantity * c.unit_price_target) / 100) *
                                                    (c.tax_rate_id
                                                        ? store.get('account.tax_rates')[c.tax_rate_id].rate
                                                        : 0)),
                                        0
                                    ),
                                    2,
                                    true,
                                    form.target_currency
                                )
                            }}
                        </th>
                        <th colspan="2"></th>
                    </tr>
                </tfoot>
            </template>
        </DataTable>
        <Button
            type="is-link"
            class="tw-w-full tw-flex tw-gap-1 tw-items-center tw-pb-3"
            :disabled="form.credit_note_line_items.length >= invoiceLineItemCount"
            @click="addLineItem"
        >
            <i class="mdi mdi-plus"></i>
            Add line item
        </Button>

        <template #footer>
            <Button type="is-success" :disabled="!creditNoteIsValid" @click="create()">Raise Credit Note</Button>
        </template>
    </Modal>
</template>

<script setup>
import { computed, onMounted, reactive, ref, useTemplateRef } from 'vue';
import { useDataStore } from '@/js/stores/DataStore.js';
import Modal from '@/js/components/Modal.vue';
import TaxRateSelector from '@/js/components/controls/TaxRateSelector.vue';
import Button from '@/js/components/controls/Button.vue';
import FormField from '@/js/components/widgets/FormField.vue';
import Textbox from '@/js/components/controls/Textbox.vue';
import { toast, formatCurrency, localise } from '@/js/utils.js';
import DataTable from '@/js/components/tables/DataTable.vue';
import EditableText from '@/js/components/controls/EditableText.vue';
import Select from '@/js/components/controls/Select.vue';
import NumberInput from '@/js/components/controls/NumberInput.vue';
import DateInput from '@/js/components/DateInput.vue';
import { route } from 'ziggy-js';
import { axios } from '@/js/utils/axios.js';
import { capturePosthogEvent } from '@/js/composables/usePosthog.js';

const store = useDataStore();
const isLoading = ref(false);
const eventId = ref(null);
const invoice = ref(null);
const modal = useTemplateRef('modal');
const event = computed(() => store.get(`account.events.${eventId.value}`));

const lineItemTemplate = ref({
    creditable_type: null,
    creditable_id: null,
    description: null,
    quantity: 0,
    unit_price_target: 0,
    tax_rate_id: null,
});

const endpoint = computed(() =>
    route('api.account.event.creditNotes.create', {
        event: eventId.value,
    })
);

const form = reactive({
    creditable_type: null,
    creditable_id: null,
    date: null,
    description: null,
    conversion_rate: 1,
    target_currency: event.value?.currency_code,
    credit_note_line_items: [],
});

const invoiceLineItemCount = computed(() => {
    return invoice.value?.line_items?.length ?? 0;
});

const availableInvoiceLineItems = computed(() => {
    if (!invoice.value || !invoice.value.line_items) {
        return [];
    }

    return invoice.value.line_items.map((item) => ({
        ...item,
        alreadySelected: form.credit_note_line_items.some((creditItem) => creditItem.creditable_id === item.id),
    }));
});

async function open(type) {
    form.credit_note_line_items = [JSON.parse(JSON.stringify(lineItemTemplate.value))];
    if (!type || !['CustomerInvoice', 'SupplierInvoice'].includes(type)) {
        toast('Error', 'Invalid type provided when attempting to create a credit note', 'error');
        return;
    }

    if (!invoice.value) {
        toast('Error', 'Invalid invoice provided when attempting to credit a credit note', 'error');
        return;
    }
    form.creditable_type = `App\\Models\\${type}`;
    form.creditable_id = invoice.value.id;
    form.date = new Date().toISOString().split('T')[0];
    form.description = `Credit note for ${type === 'CustomerInvoice' ? 'customer' : 'supplier'} invoice #${invoice.value.invoice_number}`;
    form.conversion_rate = await getConversionRate(invoice.value.target_currency ?? event.value?.currency_code);
    form.target_currency = invoice.value.target_currency ?? event.value?.currency_code;

    const [creditableType, unitPriceKey] =
        type === 'CustomerInvoice'
            ? ['App\\Models\\CustomerInvoiceLineItem', 'unit_price']
            : ['App\\Models\\SupplierInvoiceLineItem', 'unit_price_target'];

    form.credit_note_line_items[0].creditable_type = creditableType;

    if (invoice.value.line_items.length === 1) {
        form.credit_note_line_items[0].creditable_id = invoice.value.line_items[0].id;
        form.credit_note_line_items[0].description = invoice.value.line_items[0].description;
        form.credit_note_line_items[0].unit_price_target = formatUnitPriceTarget(
            invoice.value.line_items[0],
            unitPriceKey
        );
        form.credit_note_line_items[0].quantity = invoice.value.line_items[0].quantity;
        form.credit_note_line_items[0].tax_rate_id = invoice.value.line_items[0].tax_rate_id;
    }
    modal.value?.open();
}

const addLineItem = () => {
    if (form.credit_note_line_items.length >= invoiceLineItemCount.value) {
        return;
    }

    const newLineItem = JSON.parse(JSON.stringify(lineItemTemplate.value));

    const [creditableType, unitPriceKey] =
        form.creditable_type === 'App\\Models\\CustomerInvoice'
            ? ['App\\Models\\CustomerInvoiceLineItem', 'unit_price']
            : ['App\\Models\\SupplierInvoiceLineItem', 'unit_price_target'];

    newLineItem.creditable_type = creditableType;

    if (invoice.value.line_items.length === 1) {
        newLineItem.creditable_id = invoice.value.line_items[0].id;
        newLineItem.description = invoice.value.line_items[0].description;
        newLineItem.unit_price_target = formatUnitPriceTarget(invoice.value.line_items[0], unitPriceKey);
        newLineItem.quantity = invoice.value.line_items[0].quantity;
        newLineItem.tax_rate_id = invoice.value.line_items[0].tax_rate_id;
    }

    if (
        form.credit_note_line_items.length === invoice.value.line_items.length - 1 &&
        form.credit_note_line_items.every((item) => !!item.creditable_id)
    ) {
        const lineItemToCopy = invoice.value.line_items.find(
            (item) => !form.credit_note_line_items.some((creditItem) => creditItem.creditable_id === item.id)
        );

        newLineItem.creditable_id = lineItemToCopy.id;
        newLineItem.description = lineItemToCopy.description;
        newLineItem.unit_price_target = formatUnitPriceTarget(lineItemToCopy, unitPriceKey);
        newLineItem.quantity = lineItemToCopy.quantity;
        newLineItem.tax_rate_id = lineItemToCopy.tax_rate_id;
    }

    form.credit_note_line_items.push(newLineItem);
};

const creditNoteIsValid = computed(() => {
    return (
        form.credit_note_line_items.length > 0 &&
        form.credit_note_line_items.every(
            (item) =>
                item.creditable_id &&
                item.unit_price_target > 0 &&
                item.quantity > 0 &&
                (item.tax_rate_id || item.tax_rate_id === null)
        ) &&
        form.date &&
        form.target_currency
    );
});

function close() {
    form.creditable_type = null;
    form.creditable_id = null;
    form.date = null;
    form.description = null;
    form.conversion_rate = 1;
    form.target_currency = event.value?.currency_code;
    form.credit_note_line_items = [];
    modal.value.close();
}

async function create() {
    isLoading.value = true;
    try {
        await axios.post(endpoint.value, form);
        if (form.creditable_type === 'App\\Models\\SupplierInvoice') {
            capturePosthogEvent('supplier_credit_note_created');
        } else if (form.creditable_type === 'App\\Models\\CustomerInvoice') {
            capturePosthogEvent('customer_credit_note_created');
        }
        toast('Credit Note Raised', 'Your credit note has been raised successfully', 'success');
        Eventbus.$emit('reloadInvoice');
        close();
        Eventbus.$emit('reload:purchaseorders');
    } finally {
        isLoading.value = false;
    }
}

const getSubtotal = (lineItem) => {
    return (
        (lineItem.quantity ? lineItem.quantity : 0) *
        (lineItem.unit_price_target ? parseFloat(lineItem.unit_price_target) : 0)
    );
};

const formatUnitPriceTarget = (lineItem, unitPriceKey) => {
    const unitPrice = Number(lineItem[unitPriceKey]);

    return (Math.round((Number.isFinite(unitPrice) ? unitPrice : 0) * 100) / 100).toFixed(2);
};

const getTotal = (lineItem) => {
    const subtotal = getSubtotal(lineItem);
    if (!lineItem.tax_rate_id) {
        return subtotal;
    }
    return subtotal + (subtotal / 100) * store.get('account.tax_rates')[lineItem.tax_rate_id].rate;
};

const openCurrencyConverter = () => {
    Eventbus.$emit('currencyConverter');
};
const getConversionRate = (to = null) => {
    axios
        .get(
            route('api.currency.convert', {
                inputtext: '1',
                convert_from: event.value?.currency_code,
                convert_to: to ?? form.target_currency,
            })
        )
        .then(({ data }) => (form.conversion_rate = data.rate));
};

const onLineItemSelect = (v, row) => {
    const item = availableInvoiceLineItems.value.find((item) => v === item.id);

    row.creditable_id = item.id;
    row.description = item.description;
    const unitPriceKey = form.creditable_type === 'App\\Models\\CustomerInvoice' ? 'unit_price' : 'unit_price_target';
    row.unit_price_target = formatUnitPriceTarget(item, unitPriceKey);
    row.quantity = item.quantity;
    row.tax_rate_id = item.tax_rate_id;
};

onMounted(() => {
    Eventbus.$on('raiseCreditNote', (payload) => {
        eventId.value = payload.eventId;
        invoice.value = payload.invoice;
        open(payload.type);
    });
});
</script>
